/**
 * Input-mode handling for every scan field on the handheld.
 *
 * When a step advances, its scan field must be focused immediately so the
 * BT-A600 hardware wedge can enter the barcode, but the on-screen keyboard must
 * stay hidden so it doesn't cover the small (480x800) screen. A wedge scanner
 * delivers HID keystrokes regardless of `inputMode`, so `"none"` keeps scanning
 * working while suppressing the soft keyboard. Only an explicit tap on the field
 * switches to `"text"`, which reveals the keyboard for manual entry.
 *
 * (Ported verbatim from Operator UI `toteInputMode.ts`, renamed generically
 * because it now guards every scan field, not just the tote-mapping one.)
 */
export type ScanInputMode = "none" | "text";

/** Auto-focused for scanning; on-screen keyboard suppressed. */
export const SCAN_WEDGE_INPUT_MODE: ScanInputMode = "none";

/** Manual entry after an explicit tap; on-screen keyboard allowed. */
export const SCAN_MANUAL_INPUT_MODE: ScanInputMode = "text";

/**
 * Resolve the field's `inputMode` from whether the operator has explicitly
 * tapped it. Programmatic auto-focus (for the scanner) leaves `hasTapped`
 * false, so the keyboard stays hidden until a real tap flips it.
 */
export function resolveScanInputMode(hasTapped: boolean): ScanInputMode {
  return hasTapped ? SCAN_MANUAL_INPUT_MODE : SCAN_WEDGE_INPUT_MODE;
}
